import React from 'react';
import { type Editor, useEditorState } from '@tiptap/react';
import { Bold } from '@/components/Icons/Bold';
import { Italic } from '@/components/Icons/Italic';
import { LinkIcon } from '@/components/Icons/LinkIcon';
import { Underline } from '@/components/Icons/Underline';
import { Unlink } from '@/components/Icons/Unlink';
import { tv } from '@/utils/tv';

const container = tv({
  base: 'flex w-full items-center gap-4',
  variants: {
    isEmbedded: {
      true: 'py-2',
      false: 'rounded-[60px] bg-on-primary-surface px-4 py-2',
    },
    isCompact: { true: 'py-1' },
  },
});

const iconButton = tv({
  base: 'flex size-6 cursor-pointer items-center justify-center',
  variants: {
    isActive: { true: 'text-primary', false: 'text-muted-foreground' },
  },
});

interface Props {
  editor: Editor | null;
  isEmbedded?: boolean;
  isCompact?: boolean;
  onLinkClick: () => void;
}

export const ComposeToolbar: React.FC<Props> = ({
  editor,
  isEmbedded = false,
  isCompact = false,
  onLinkClick,
}) => {
  const iconSize = isEmbedded || isCompact ? 'size-5' : 'size-6';
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      isBold: instance?.isActive('bold') ?? false,
      isItalic: instance?.isActive('italic') ?? false,
      isUnderline: instance?.isActive('underline') ?? false,
      isLink: instance?.isActive('link') ?? false,
    }),
  });

  if (!editor) {
    return null;
  }

  return (
    <div className={container({ isEmbedded, isCompact })}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onLinkClick}
          className={iconButton({ isActive: state?.isLink })}
        >
          <LinkIcon className={iconSize} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetLink().run()}
          className={iconButton({ isActive: false })}
        >
          <Unlink className={iconSize} />
        </button>
      </div>

      <span className="h-5 w-px bg-divider" />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={iconButton({ isActive: state?.isBold })}
        >
          <Bold className={iconSize} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={iconButton({ isActive: state?.isItalic })}
        >
          <Italic className={iconSize} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={iconButton({ isActive: state?.isUnderline })}
        >
          <Underline className={iconSize} />
        </button>
      </div>
    </div>
  );
};
